import type { FieldValues } from '@sravni/react-silent-authentication-form';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { QUERY_PARAMS } from 'constants/queryNames';

import { useAppDispatch, useAppSelector } from 'shared/lib';
import {
  sendEventAuthorizationSuccess,
  sendEventChangePhoneNumber,
  sendEventCloseSMSPopupClick,
  sendEventConsentsScreenButtonClick,
  sendEventGetCodeBySms,
  sendEventInvalidSmsCode,
  sendEventOnDisableAgreementPersonalData,
  sendEventOnEnableAgreementAdvertising,
  sendEventOpenConsentsScreen,
  sendEventRequestNewCode,
} from 'shared/lib/sendGAEvents';
import { UserManagerFactory } from 'shared/lib/UserManager';
import type { AgreementsOnClick, CustomAuthInlineFormProps } from 'shared/ui/CustomAuthInlineForm';
import { AdvertisingBanner, CustomAuthInlineForm } from 'shared/ui/CustomAuthInlineForm';

import { getUserInfo } from 'entities/user';
import { isWLSelector } from 'entities/whiteLabels';
import { setWhiteLabelUserDataToken } from 'entities/whiteLabelUserData';
import { useGetUserData } from 'src/client/entities/creditForm';

import { AuthFormConfig } from 'features/UpdateContactsInfo/AuthForm.config';

import styles from './AuthForm.module.scss';
import { onFailedLoadingUserData, onLoginFailed } from './helpers';
import type { AuthFormProps } from './types';

export const AuthFormHoc: FC<AuthFormProps & { FormComponent?: React.FC<any> }> = (props) => {
  const {
    authDefaultValues,
    additionalProps,
    FormComponent,
    isOTPButtonStickyEnabled = true,
    getOTPButtonProps = {},
    validationSchema,
    onAuthorizationSuccess,
    isEnabledEmailField = false,
    ...rest
  } = props;
  const { isAnyFormStepOpenedInPopup, analyticsOverrides } = additionalProps || {};
  const { metaInfo, texts: AuthFormTexts } = AuthFormConfig;
  const router = useRouter();
  const { query } = router;
  const restoreGuid = query[QUERY_PARAMS.RESTORE_GUID] as string | undefined;
  const isWL = useAppSelector(isWLSelector);
  const dispatch = useAppDispatch();
  const [getUser] = getUserInfo();
  const [getUserData] = useGetUserData();

  const handleGetUser = useCallback(
    async (userInfo: FieldValues & Record<string, unknown>) => {
      try {
        sendEventAuthorizationSuccess('sravni', analyticsOverrides);
        if (isWL) {
          const accessToken = await new UserManagerFactory().getAccessToken();
          dispatch(setWhiteLabelUserDataToken(accessToken));
        }
        await getUser();
        if (!restoreGuid) {
          await getUserData();
        }
      } catch (error) {
        onFailedLoadingUserData();
      } finally {
        onAuthorizationSuccess?.(userInfo);
      }
    },
    [getUser, dispatch, getUserData, restoreGuid, isWL, onAuthorizationSuccess, analyticsOverrides],
  );

  const onChangeAgreementAdvertising: NonNullable<CustomAuthInlineFormProps['onChangeAgreementAdvertising']> =
    useCallback(
      (event) => {
        const { checked: isChecked } = event?.target || {};
        if (isChecked) {
          sendEventOnEnableAgreementAdvertising(analyticsOverrides);
        }
      },
      [analyticsOverrides],
    );

  const onChangeAgreementPersonalData: NonNullable<CustomAuthInlineFormProps['onChangeAgreementPersonalData']> =
    useCallback(
      (event) => {
        const { checked: isChecked } = event?.target || {};
        if (!isChecked) {
          sendEventOnDisableAgreementPersonalData(analyticsOverrides);
        }
      },
      [analyticsOverrides],
    );

  const onConsentsSubmitClick = useCallback(
    (data: AgreementsOnClick) => {
      const { isAgreementAdvertising, isAgreementPersonalData, isSubmitted } = data;
      sendEventConsentsScreenButtonClick(
        isAgreementAdvertising,
        isAgreementPersonalData,
        isSubmitted,
        analyticsOverrides,
      );
    },
    [analyticsOverrides],
  );

  const onConsentsScreenOpen = useCallback(() => sendEventOpenConsentsScreen(analyticsOverrides), [analyticsOverrides]);
  const onSendCodeBySmsClick = useCallback(() => sendEventGetCodeBySms(analyticsOverrides), [analyticsOverrides]);
  const onChangePhoneNumber = useCallback(() => sendEventChangePhoneNumber(analyticsOverrides), [analyticsOverrides]);
  const onGetNewOTPClick = useCallback(() => sendEventRequestNewCode(analyticsOverrides), [analyticsOverrides]);
  const onInvalidSmsCodeError = useCallback(() => sendEventInvalidSmsCode(analyticsOverrides), [analyticsOverrides]);
  const onCloseSMSPopup = useCallback(() => sendEventCloseSMSPopupClick(analyticsOverrides), [analyticsOverrides]);

  return (
    <CustomAuthInlineForm
      isEnabledEmailField={isEnabledEmailField}
      metaInfo={metaInfo}
      advertisingCheckboxLabel={AuthFormTexts.advertisingCheckboxLabel}
      userManager={new UserManagerFactory().createUserManager()}
      consentsScreenTitle={AuthFormTexts.consentsScreenTitle}
      advertisingBanner={
        <AdvertisingBanner
          title={AuthFormTexts.bannerTitle}
          list={AuthFormTexts.bannerAdsTextsList}
        />
      }
      additionalValidationSchema={validationSchema}
      defaultValues={authDefaultValues as unknown as FieldValues}
      getOTPButtonProps={{
        isInlineButton: true,
        className: styles.otpButton,
        ...getOTPButtonProps,
        additionalValidationFields: Object.keys(validationSchema.fields),
        onClick: onSendCodeBySmsClick,
      }}
      isOTPButtonSticky={!isAnyFormStepOpenedInPopup}
      isEnabledOTPButtonSticky={isOTPButtonStickyEnabled}
      onLogin={handleGetUser as unknown as CustomAuthInlineFormProps['onLogin']}
      onLoginFailed={onLoginFailed}
      onChangePhoneClick={onChangePhoneNumber}
      onChangeAgreementAdvertising={onChangeAgreementAdvertising}
      onChangeAgreementPersonalData={onChangeAgreementPersonalData}
      onConsentsScreenOpen={onConsentsScreenOpen}
      onConsentsSubmitClick={onConsentsSubmitClick}
      onGetNewOTPClick={onGetNewOTPClick}
      onInvalidSmsCodeError={onInvalidSmsCodeError}
      onClose={onCloseSMSPopup}
    >
      <CustomAuthInlineForm.Before>{FormComponent && <FormComponent {...rest} />}</CustomAuthInlineForm.Before>
    </CustomAuthInlineForm>
  );
};
